import { Call, Document, UserSquare } from 'iconsax-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import MicrophoneBoldIcon from '@/assets/images/icons/microphone-bold.svg?react';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import { SafeImage } from '@/components/common/safe-image';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import useUserDisplayInfo from '@/hooks/users/use-user-display-info';
import {
  ConversationMessageContentType,
  type ConversationMessageTypeUnion,
  conversationMessageUnionScheme,
} from '@/models/conversations/conversation';
import { contactMessageScheme, inviteMessageScheme } from '@/models/messages/message-type';
import ROUTES from '@/routes';
import { useMessageStore } from '@/stores/message';
import { addToast } from '@/stores/toast';
import { isDeleted, isDeletedById } from '@/utils/check-messages-status';
import { getPosterUrl } from '@/utils/media-url';
import cn from '@/utils/merge-class-names';
import { isGroupMessage } from '@/utils/message';
import { generateSessionKey } from '@/utils/session';

interface Props {
  quote: ConversationMessageTypeUnion | string | undefined;
  className?: string;
}

export function MessageQuote({ className, quote }: Props) {
  const { t } = useTranslation();
  const { resetSelectedMessages } = useConversationStore();
  const { handleInteraction: onInteraction } = useConversationHandlers();
  const navigate = useNavigate();
  const messages = useMessageStore((s) => s.messages);
  const { type, id } = useConversationStore();
  const placement = typeof quote === 'string' ? 'message' : 'form';

  const quoteContent = useMemo(() => {
    if (!quote) return undefined;

    let parsed: unknown = quote;
    if (typeof quote === 'string') {
      try {
        parsed = JSON.parse(quote);
      } catch {
        parsed = {};
      }
    }

    const { success, data } = conversationMessageUnionScheme.safeParse(parsed);

    return success ? data : undefined;
  }, [quote]);

  const isGroup = isGroupMessage(quoteContent);

  const { name: displayName, avatarUrl } = useUserDisplayInfo(quoteContent?.UserID, {
    groupId: isGroup ? quoteContent?.GroupID : undefined,
    defaultName: quoteContent?.UserNickName,
    defaultAvatar: quoteContent?.UserHeadImg,
  });

  const handleViewQuote = () => {
    if (!quoteContent) return;

    const sessionKey = generateSessionKey(type, id);
    const messageExists = messages.has(quoteContent.ID);
    const messageDeleted = isDeletedById(sessionKey, quoteContent.ID) || (messageExists && isDeleted(quoteContent));

    if (messageDeleted) {
      addToast({
        type: 'warning',
        message: t('conversation.message.content-not-exist'),
      });
      return;
    }

    resetSelectedMessages([quoteContent]);

    const targetPath =
      type === 'group'
        ? ROUTES.MESSAGES.GROUPS.$buildPath({
            params: { id },
            searchParams: { search: quoteContent.ID },
          })
        : ROUTES.MESSAGES.SESSIONS.$buildPath({
            params: { id },
            searchParams: { search: quoteContent.ID },
          });

    navigate(targetPath, { replace: true });

    switch (quoteContent.MsgType) {
      case ConversationMessageContentType.Contact:
        onInteraction('contact');
        break;
      default:
        // onInteraction('preview');
        break;
    }
  };

  if (!quoteContent) return null;

  let prefix: ReactNode;
  let description: ReactNode;
  let icon: ReactNode;
  switch (quoteContent.MsgType) {
    case ConversationMessageContentType.Image:
      prefix = (
        <SafeImage
          src={quoteContent.Message}
          className='size-8 bg-cover'
        />
      );
      description = t('message.picture');
      break;
    case ConversationMessageContentType.Video:
      prefix = (
        <SafeImage
          src={getPosterUrl(quoteContent.Message)}
          className='size-8 bg-cover'
        />
      );
      description = t('message.video');
      break;
    case ConversationMessageContentType.Audio:
      icon = <MicrophoneBoldIcon className='size-4 fill-neutral-0' />;
      description = t('message.audio');
      break;
    case ConversationMessageContentType.Emoticon:
      prefix = (
        <SafeImage
          className='size-8 bg-contain bg-transparent'
          src={quoteContent.Message}
        />
      );
      description = t('message.emoticon');
      break;
    case ConversationMessageContentType.File:
      {
        const [, filename = 'download.txt'] = quoteContent.Message.split('|');
        icon = <Document className='size-4' />;
        description = filename;
      }
      break;
    case ConversationMessageContentType.Contact:
      {
        const { success, data } = contactMessageScheme.safeParse(JSON.parse(quoteContent.Message));
        icon = (
          <UserSquare
            variant='Bold'
            className='size-4'
          />
        );
        description = success ? data.NickName : t('contact.error.damaged');
      }
      break;
    case ConversationMessageContentType.Call:
      icon = <Call className='size-4' />;
      description = t('message.call');
      break;
    case ConversationMessageContentType.Invite:
      {
        const { success, data } = inviteMessageScheme.safeParse(JSON.parse(quoteContent.Message));
        description = success ? `${t('message.invite')} ${data.GroupName}` : t('contact.error.damaged');
      }
      break;
    default:
      prefix =
        placement === 'form' ? (
          <AvatarWithStatus
            className='size-8'
            imageUrl={avatarUrl}
            size={[32, 32]}
          />
        ) : undefined;
      description = quoteContent.Message;
      break;
  }
  const sessionKey = generateSessionKey(type, id);
  const messageDeleted =
    isDeletedById(sessionKey, quoteContent.ID) || (messages.get(quoteContent.ID) && isDeleted(quoteContent));
  if (messageDeleted) {
    description = t('conversation.message.quote-content-not-exist');
  }

  return (
    <button
      type='button'
      className={cn(
        'py-1.5 px-2 flex h-full items-center gap-2 rounded border-l-4 border-l-primary bg-base-100 group/quote group-data-sender:chat-quote-primary',
        className,
      )}
      onClick={handleViewQuote}>
      {prefix}
      <div className='flex max-w-70vw flex-1 items-stretch flex-col '>
        <h4 className='text-body2 desktop:text-body3 text-left text-primary  group-data-sender:text-body group-data-quote/form:text-black group-data-quote/form:font-bold'>
          {displayName}
        </h4>

        <div className='flex gap-1 items-center'>
          {icon}
          <span className='text-body2 desktop:text-sm break-all text-left group-data-sender:text-body line-clamp-2'>
            {description}
          </span>
        </div>
      </div>
    </button>
  );
}
